import * as Sentry from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { BrowserDeniedNotice } from "@/components/notifications/BrowserDeniedNotice.tsx";
import { canGetIphoneApp, GetIphoneApp } from "@/components/notifications/GetIphoneApp.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { errorToast } from "@/components/ui/toast.tsx";
import { useNotification } from "@/hooks/useBrowserNotifications/NotificationContext.tsx";
import { pushPromptQuery, useRecordPushPromptShown } from "@/shared/pushPromptQueries.ts";
import { reminderQueries } from "@/shared/reminderQueries.ts";

/**
 * The push prompt on the web (`docs/prds/onboarding.md`, The push prompt), on
 * app open: for a user nothing can push to, it offers this browser's
 * notifications and the iPhone app.
 *
 * The server decides whether it is due; this acts on the first answer of the
 * load only, so Settings refreshing the same answer after a toggle can't open
 * it, and reports the showing back. Inside the iOS app's WebView the native app
 * asks instead.
 */
export const PushPromptModal = () => {
  const isWebView = typeof window !== "undefined" && window.isWebView === true;
  const { permission, requestPermission, isSupported } = useNotification();
  // A device with neither a browser that takes push nor a way to the app has nothing to offer.
  const canOffer = isSupported || canGetIphoneApp();
  const canAsk = !isWebView && canOffer;
  const { data } = useQuery({ ...pushPromptQuery("web"), enabled: canAsk });
  const { mutate: recordShown } = useRecordPushPromptShown("web", {
    onError: (error) => Sentry.captureException(error),
  });
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);
  const answered = useRef(false);

  useEffect(() => {
    if (!canAsk || !data || answered.current) return;
    answered.current = true;
    if (!data.due) return;
    setIsOpen(true);
    recordShown();
  }, [canAsk, data, recordShown]);

  const turnOnBrowser = async () => {
    setIsEnabling(true);
    try {
      const granted = await requestPermission();
      if (granted) {
        setIsOpen(false);
      } else if ("Notification" in window && Notification.permission === "granted") {
        // Allowed but not subscribed: the subscription failed, not the user's choice.
        errorToast("Couldn't turn on notifications. Please try again.");
      }
    } finally {
      setIsEnabling(false);
      // A new subscription is a push route, for the settings page and reminders alike.
      void queryClient.invalidateQueries({ queryKey: pushPromptQuery("web").queryKey });
      void queryClient.invalidateQueries({ queryKey: reminderQueries.channels().queryKey });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="flex flex-col gap-6 sm:max-w-md">
        <DialogHeader className="items-center text-center">
          <DialogTitle className="text-xl font-semibold">
            Get notified when Okthx needs you
          </DialogTitle>
          <DialogDescription>
            Calendar reminders, things to approve and finished tasks need somewhere to reach you.
          </DialogDescription>
        </DialogHeader>
        {isSupported && (
          <section className="flex flex-col gap-3 text-center">
            <div>
              <div className="text-sm font-medium">Browser notifications</div>
              <div className="text-sm text-muted-foreground">On this computer.</div>
            </div>
            {permission === "denied" ? (
              <BrowserDeniedNotice />
            ) : (
              <Button
                onClick={() => void turnOnBrowser()}
                disabled={isEnabling}
                data-statsig-name="push-prompt-browser"
              >
                Turn on notifications
              </Button>
            )}
          </section>
        )}
        {canGetIphoneApp() && (
          <section className="flex flex-col items-center gap-3 text-center">
            <div>
              <div className="text-sm font-medium">Okthx for iPhone</div>
              <div className="text-sm text-muted-foreground">Reminders reach you anywhere.</div>
            </div>
            <GetIphoneApp />
          </section>
        )}
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setIsOpen(false)}
            data-statsig-name="push-prompt-not-now"
          >
            Not now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
